import { TimeRanges } from '@/views/components/CopyTrade/Follower/PortfolioCopies/PortfolioCopies';
import Highcharts from 'highcharts';
import HighchartsReact, { HighchartsReactProps } from 'highcharts-react-official';

import classNames from 'classnames/bind';
import styles from './LineChartElement.module.scss';
import { useTheme } from '@mui/material';
const cx = classNames.bind(styles);

type SeriesProperty = {
    id: string,
    name: string,
    data?: any[],
    color?: string,
    isSupportDashStyle?: boolean,
    dashStyle?: "Line" | "Dash"
    marker: {
        enabled: boolean
    },
    tickInterval?: number
    yAxis?: number
    type?: string
    fillColor?: {},
    lineWidth?: number
}

type Coordinate = "XAxis" | "YAxis"

export type DataSeries = { type: Coordinate, value: any[], id: string }

export type DataSeriesArray = DataSeries[]

interface Props extends HighchartsReactProps {
    dataSeries: DataSeriesArray;
    series: SeriesProperty[];
    width?: number | string | null;
    height?: number | string | null;
    isShowLegend?: boolean;
    isShowXAxis?: boolean;
    isShowYAxis?: boolean;
    timeRange?: TimeRanges;
    idCompare?: string;

    numberDecimal?: number
}
const LineChartElement = (props: Props) => {
    const theme = useTheme()
    const { dataSeries, timeRange, series, idCompare, numberDecimal, ...rest } = props

    // console.log('ha_check_LineChartElement', dataSeries);

    const findXAxis = dataSeries.find((item: DataSeries) => {
        return item.type === 'XAxis'
    })

    const findYAxis = dataSeries.filter((item: DataSeries) => {
        return item.type === 'YAxis'
    })

    const dataForCompare = findYAxis.find((item: DataSeries) => {
        return item.id === idCompare
    })

    const dataLength = findXAxis?.value?.length ?? 1
    const firstPoint = dataForCompare?.value?.[0] ?? 0;
    const lastPoint = dataForCompare?.value?.[dataLength - 1];
    const lineColor = firstPoint > lastPoint ? theme.palette.chart?.red : theme.palette.chart?.green

    const genSeries = () => {
        return series.map((item: SeriesProperty, index: number) => {
            const data = findYAxis.find((itemYAxis: DataSeries) => {
                return itemYAxis.id === item.id
            })
            const raw: SeriesProperty = {
                id: item.id,
                name: item.name,
                data: data?.value ?? [],
                color: item.color ?? theme.palette.chart?.yellow,
                marker: {
                    enabled: false,
                },
                yAxis: item?.yAxis ?? 0,
            }

            if (item?.type) {
                raw.type = item?.type
                raw.fillColor = {
                    linearGradient: {
                        x1: 0,
                        y1: 0,
                        x2: 0,
                        y2: 1
                    },
                    stops: [
                        [0, lineColor === theme.palette.chart?.green ? theme.palette.chart?.greenLighter : theme.palette.chart?.redLighter],
                        [1, lineColor === theme.palette.chart?.green ? theme.palette.chart?.greenDarker : theme.palette.chart?.redDarker],
                    ]
                }
            }
            if (item?.lineWidth) {
                raw.lineWidth = item?.lineWidth
            }

            if (item.isSupportDashStyle === true) {
                raw.dashStyle = props?.isShowYAxis ? (raw?.dashStyle ?? 'Line') : 'Dash'
            }

            if (item.id === idCompare) {
                raw.color = lineColor
            }

            return raw
        })
    }

    const genYAxis = () => {
        const listYAxis: any[] = []
        series.forEach((item: SeriesProperty, index: number) => {
            const raw: any = {
                title: {
                    text: null
                },
                gridLineDashStyle: 'Dash',
                gridLineWidth: props?.isShowYAxis ? 1 : 0,
                gridLineColor: theme.palette.text.secondary,
                labels: {
                    enabled: props?.isShowYAxis ?? true,
                    style: {
                        color: theme.palette.text.secondary,
                    },
                },
            }

            if (item?.tickInterval) {
                raw.tickInterval = item?.tickInterval
            }

            if (item?.yAxis === 1) {
                raw.yAxis = item?.yAxis ?? 0
                raw.opposite = true
            }
            listYAxis.push(raw)
        })

        return listYAxis
    }

    const cookedLabelStep = (timeRange: string = '1M'): number => {
        let result = 3
        switch (timeRange) {
            case '1-M':
                result = 3
                break;
            case '3-M':
                result = 9
                break;
            case '6-M':
                result = 18
                break;
            case '1-Y':
                result = 36
                break;
            default:
                result = 3
        }
        return result
    }

    const options = {
        chart: {
            type: 'spline',
            backgroundColor: 'transparent',
            width: props?.width === null ? null : ((368 + 410) / 4),  // Set chart width in pixels
            height: props?.height === null ? null : 80  // Set chart height in pixels
        },
        title: {
            text: null
        },
        xAxis: {
            categories: findXAxis?.value,
            lineWidth: props?.isShowXAxis === false ? 0 : 1,
            labels: {
                step: cookedLabelStep(timeRange),
                enabled: props?.isShowXAxis ?? true,
                style: {
                    color: theme.palette.text.secondary,
                },
            },
            crosshair: {
                dashStyle: 'Dash',
                color: theme.palette.text.secondary,
                width: 1
            },
        },
        yAxis: genYAxis(),
        legend: {
            symbolWidth: 17,
            symbolHeight: 8,
            symbolRadius: 3,
            symbolPadding: 10,

            enabled: props?.isShowLegend ?? true,
            itemStyle: {
                color: theme.palette.text.secondary,
                fontSize: '12px',
                lineHeight: '12px'
            },
            itemHoverStyle: {
                color: theme.palette.text.secondary,
                fontWeight: 'normal',
                cursor: 'pointer'
            }
        },
        tooltip: {
            shared: true,
            crosshairs: true,
            backgroundColor: theme.palette.secondary.main,
            style: {
                color: theme.palette.text.primary,
            },
            formatter: function (): string {
                let tooltip = `<b>${(this as any).x}</b><br/>`;
                (this as any).points.forEach((point: any) => {
                    tooltip += `<span style="color:${point.series.color}">\u25CF</span> ${point.series.name}: <b>${Highcharts.numberFormat(point.y, numberDecimal ?? 0, '.', ',')}</b><br/>`;
                });
                return tooltip;
            }
        },
        series: genSeries(),
        exporting: {
            enabled: false,
        },
    };

    // console.log('ha_check_option', options);

    return (
        <HighchartsReact
            containerProps={{ class: cx('line-chart-custom') }}
            highcharts={Highcharts}
            options={options}
            {...rest}
        />
    );
};

export default LineChartElement;